import { useApolloClient } from '@apollo/client';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';

import { useGraphRef } from '../../hooks/useGraphRef';
import { GraphBehavioralFlags } from '../../variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';

import { CannotRunWithoutVariantEndpointModal } from './cannotRunWithoutVariantEndpointModal/CannotRunWithoutVariantEndpointModal';
import { CannotSetVariantEndpointModal } from './cannotSetVariantEndpointModal/CannotSetVariantEndpointModal';
import { RegistryConnectionSettingsModal } from './registryConnectionSettingsModal/RegistryConnectionSettingsModal';
import { SandboxConnectionSettingsModal } from './sandboxConnectionSettingsModal/SandboxConnectionSettingsModal';

/**
 * Contextual information on which graph and variant you're going to try to
 * mutate the URL for
 */
export interface LinkEndpointModalData {
  openingIntent: 'Set url' | 'Run query';
}

/**
 * This is used to set `location.state` so that the modal for showing the edit
 * url survives refreshes
 *
 * The state is optional so this is can also be `undefined`
 */
type LocationState =
  | undefined
  | {
      linkEndpointModalData?: LinkEndpointModalData;
    };

export const ConnectionSettingsModal = ({
  isOpen,
  onClose,
  explorerBehavioralFlags,
  graphBehavioralFlags,
}: {
  isOpen: boolean;
  onClose: React.Dispatch<React.SetStateAction<void>>;
  explorerBehavioralFlags?: ExplorerBehavioralFlags;
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  const location = useLocation<LocationState>();
  /**
   * Data used to populate and respond to the modal to change a variant's URL.
   */
  const linkEndpointModalData = location.state?.linkEndpointModalData;
  const client = useApolloClient();
  const graphRef = useGraphRef();

  return (
    <>
      <PermissionGuard
        graphId={graphRef?.graphId}
        graphVariantPermissions="canManageExplorerSettings"
        graphVariant={graphRef?.graphVariant}
        fallback={() =>
          linkEndpointModalData?.openingIntent === 'Run query' ? (
            <CannotRunWithoutVariantEndpointModal
              isOpen={isOpen}
              onClose={onClose}
            />
          ) : (
            <CannotSetVariantEndpointModal isOpen={isOpen} onClose={onClose} />
          )
        }
      >
        {graphRef ? (
          <RegistryConnectionSettingsModal
            isOpen={isOpen}
            graphRef={graphRef}
            onClose={onClose}
            onCompleted={() => {
              onClose();
              if (graphRef) {
                client.cache.evict({
                  id: client.cache.identify({
                    __typename: 'Service',
                    id: graphRef.graphId,
                  }),
                });
              }
            }}
          />
        ) : (
          <SandboxConnectionSettingsModal
            isOpen={isOpen}
            onClose={onClose}
            explorerBehavioralFlags={explorerBehavioralFlags}
            graphBehavioralFlags={graphBehavioralFlags}
          />
        )}
      </PermissionGuard>
    </>
  );
};
